// Currency helpers — Phase 30.B switched the internal unit from EUR to Credits.
// Stripe (top-up + Connect/payout) keeps EUR for billing, but everything else
// the user sees on the platform — wallet balance, post/live prices, tips —
// is now denominated in integer Credits.
//
// Conversion rate (fixed): 1 EUR = 10 Credits, 1 Credit = €0.10.
//
// Jul 2026 · localization: `fmtCredits` and `fmtCreditsCompact` now read the
// active i18n language and print "N crediti" for IT users, "N Credits" for
// everyone else. Importing the i18n instance directly (not the React hook)
// keeps these helpers usable outside of React components.

import i18n from '../i18n';

export const CURRENCY = 'EUR';
export const CURRENCY_SYMBOL = '€';
export const CREDITS_PER_EUR = 10;
export const EUR_PER_CREDIT = 0.10;

const _creditsLabel = (v) => {
  // `i18n.language` is 'it' / 'it-IT' / 'en' / 'en-US' / etc.
  const lang = (i18n && i18n.language) ? String(i18n.language).toLowerCase() : 'en';
  return lang.startsWith('it') ? 'crediti' : 'Credits';
};

// === Credits — primary display ===
export const fmtCredits = (n) => {
  const v = Math.max(0, Math.round(Number(n || 0)));
  return `${v.toLocaleString()} ${_creditsLabel(v)}`;
};

export const fmtCreditsCompact = (n) => {
  const v = Math.max(0, Math.round(Number(n || 0)));
  const label = _creditsLabel(v);
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k ${label}`;
  return `${v} ${label}`;
};

// === EUR — kept ONLY for Stripe-side surfaces (top-up cards, payout cards,
// receipts, admin finance EUR tooltips). Do NOT use in regular UI flows. ===
export const fmtEur = (n) => `${CURRENCY_SYMBOL}${Number(n || 0).toFixed(2)}`;
export const fmtMoney = fmtEur; // legacy alias (some files still import it)
export const fmtMoneyCompact = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1000) return `${CURRENCY_SYMBOL}${(v / 1000).toFixed(1)}k`;
  return `${CURRENCY_SYMBOL}${v.toFixed(0)}`;
};

// === Conversion helpers ===
export const creditsToEur = (c) => Math.round((Number(c || 0) * EUR_PER_CREDIT) * 100) / 100;
export const eurToCredits = (e) => Math.round(Number(e || 0) * CREDITS_PER_EUR);

// === Catalogs ===
// Creator pricing dropdown — the ONLY allowed prices for paid posts, live
// tickets, tips and any future creator service. Must match the backend
// constant CREATOR_PRICE_OPTIONS in /app/backend/core/config.py.
export const CREATOR_PRICE_OPTIONS = [50, 100, 150, 200, 250, 300, 500, 750, 990];

// Wallet top-up packages — client-side FALLBACK only.
// Jul 2026: the source of truth is now the admin-managed
// `wallet_packages` MongoDB collection (see
// `backend/services/wallet_packages.py`). The Shop page loads the
// live catalogue via `/api/shop/config` on mount and overrides this
// constant; this list is only used if the config call fails.
// Nessun bonus al lancio — ogni credito ha valore economico reale.
export const TOPUP_PACKAGES = [
  { eur: 5,  credits: 50,  bonus_pct: 0 },
  { eur: 10, credits: 100, bonus_pct: 0 },
  { eur: 20, credits: 200, bonus_pct: 0 },
  { eur: 40, credits: 400, bonus_pct: 0 },
];
